import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StripeCheckout from '../lib/react-stripe-checkout';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { createTransaction } from '../actions/stripe';
import { immediateUpdate } from '../actions/user';
import { sendPostback } from '../actions/conversation';

import { getIntegration } from '../utils/app';
import { bindAll } from '../utils/functions';
import { isSupportedActionType } from '../utils/message';

import { ACTION_TYPES } from '../constants/message';
import { COLORS } from '../constants/styles';

import Loading from './CircularLoading';
import WebviewAction from './WebviewAction';

const styles = {
    inline: {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        borderWidth: '0px',
        borderTop: `1px solid ${COLORS.GRAY_GALLERY}`,
        borderRadius: '0px',
        fontWeight: 500,
        lineHeight: '1.5',
        height: '33px',
    },
};

export class ActionComponent extends Component {
    static propTypes = {
        _id: PropTypes.string.isRequired,
        actionPaymentCompletedText: PropTypes.string.isRequired,
        amount: PropTypes.number,
        businessIconUrl: PropTypes.string,
        businessName: PropTypes.string.isRequired,
        buttonColor: PropTypes.string,
        createTransaction: PropTypes.func.isRequired,
        currency: PropTypes.string,
        fallback: PropTypes.string,
        immediateUpdate: PropTypes.func.isRequired,
        inlineButton: PropTypes.bool,
        integrations: PropTypes.array.isRequired,
        isLinkColorDark: PropTypes.bool,
        isScrolledToBottom: PropTypes.bool.isRequired,
        openOnReceive: PropTypes.bool,
        messageId: PropTypes.string,
        sendPostback: PropTypes.func.isRequired,
        size: PropTypes.string,
        state: PropTypes.string,
        text: PropTypes.string.isRequired,
        type: PropTypes.string,
        unsupportedActionTypeText: PropTypes.string.isRequired,
        uri: PropTypes.string,
        user: PropTypes.object.isRequired,
    };

    static defaultProps = {
        isScrolledToBottom: false,
        openOnReceive: false,
    };

    constructor(...args) {
        super(...args);

        this.state = {
            state: this.props.state,
            hasToken: false,
        };

        bindAll(this, 'onPostbackClick', 'onStripeToken', 'onStripeClick', 'onStripeClose');
    }

    onPostbackClick(e) {
        e.preventDefault();

        this.setState({
            state: 'processing',
        });

        this.props
            .sendPostback(this.props._id)
            .then(() => {
                this.setState({
                    state: '',
                });
            })
            .catch(() => {
                this.setState({
                    state: '',
                });
            });
    }

    onStripeToken(token) {
        const { user, immediateUpdate, createTransaction } = this.props;
        this.setState({
            hasToken: true,
        });

        const promises = [];
        if (!user.email) {
            promises.push(
                immediateUpdate({
                    email: token.email,
                }),
            );
        }

        const transactionPromise = createTransaction(this.props._id, token.id)
            .then(() => {
                this.setState({
                    state: 'paid',
                });
            })
            .catch(() => {
                this.setState({
                    state: 'offered',
                });
            });

        promises.push(transactionPromise);

        return Promise.all(promises);
    }

    onStripeClick(e) {
        e.preventDefault();
        this.setState({
            state: 'processing',
        });
    }

    onStripeClose() {
        if (!this.state.hasToken) {
            this.setState({
                state: 'offered',
            });
        }
    }

    render() {
        const {
            actionPaymentCompletedText,
            amount,
            businessIconUrl,
            businessName,
            buttonColor,
            currency,
            fallback,
            integrations,
            inlineButton,
            isLinkColorDark,
            openOnReceive,
            messageId,
            size,
            text,
            type,
            unsupportedActionTypeText,
            uri,
            user,
        } = this.props;
        const { state } = this.state;

        const stripeIntegration = getIntegration(integrations, 'stripeConnect');

        let actionStyle = {};

        const loadingColor = isLinkColorDark ? COLORS.WHITE : COLORS.GRAY_TUNDORA;

        if (buttonColor) {
            actionStyle.backgroundColor = actionStyle.borderColor = `#${buttonColor}`;
        }

        if (inlineButton) {
            actionStyle.color = `#${buttonColor}`;
            actionStyle = { ...actionStyle, ...styles.inline };
        }

        // the public key is necessary to use with Checkout
        // use the link fallback if this happens
        if (type === ACTION_TYPES.BUY && stripeIntegration) {
            if (state === 'offered') {
                return (
                    <StripeCheckout
                        ComponentClass="div"
                        className="action"
                        token={this.onStripeToken}
                        stripeKey={stripeIntegration.publicKey}
                        email={user.email}
                        amount={amount}
                        currency={currency.toUpperCase()}
                        name={businessName}
                        image={businessIconUrl}
                        closed={this.onStripeClose}
                        executionContext={parent}
                    >
                        <a className="btn btn-primary" onClick={this.onStripeClick} style={actionStyle}>
                            {text}
                        </a>
                    </StripeCheckout>
                );
            } else {
                const btnStyles = ['btn'];
                const buttonText =
                    state === 'paid' ? (
                        actionPaymentCompletedText
                    ) : (
                        <Loading color={inlineButton ? `#${buttonColor}` : loadingColor} />
                    );

                if (inlineButton) {
                    actionStyle.color = `#${buttonColor}`;
                    actionStyle = { ...actionStyle, ...styles.inline };
                } else {
                    btnStyles.push(`btn-action-${state}`);
                }

                if (state === 'paid') {
                    if (inlineButton) {
                        actionStyle.color = COLORS.GRAY_NOBEL;
                        actionStyle.pointerEvents = 'none';
                    } else {
                        actionStyle = {};
                    }
                }

                return (
                    <div className="action">
                        <div className={btnStyles.join(' ')} style={actionStyle}>
                            {buttonText}
                        </div>
                    </div>
                );
            }
        } else if (type === ACTION_TYPES.POSTBACK) {
            const isProcessing = state === 'processing';
            const buttonText = isProcessing ? (
                <Loading color={inlineButton ? `#${buttonColor}` : loadingColor} />
            ) : (
                text
            );

            return (
                <div className="action">
                    <a
                        className="btn btn-primary"
                        style={actionStyle}
                        onClick={!isProcessing ? this.onPostbackClick : () => {}}
                    >
                        {buttonText}
                    </a>
                </div>
            );
        } else if (type === ACTION_TYPES.LINK || (type === ACTION_TYPES.BUY && !stripeIntegration)) {
            const isJavascript = uri.startsWith('javascript:');

            return (
                <div className="action">
                    <a
                        className="btn btn-primary"
                        href={uri}
                        target={isJavascript ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        style={actionStyle}
                    >
                        {text}
                    </a>
                </div>
            );
        } else if (type === ACTION_TYPES.WEBVIEW) {
            return (
                <div className="action">
                    <WebviewAction
                        actionStyle={actionStyle}
                        messageId={messageId}
                        openOnReceive={openOnReceive}
                        size={size}
                        text={text}
                        type={type}
                        uri={uri}
                    ></WebviewAction>
                </div>
            );
        } else if (!isSupportedActionType(type) && fallback) {
            const isJavascript = fallback.startsWith('javascript:');
            return (
                <div className="action">
                    <a
                        className="btn btn-primary"
                        href={fallback}
                        target={isJavascript ? '_self' : '_blank'}
                        rel="noopener noreferrer"
                        style={actionStyle}
                    >
                        {text}
                    </a>
                </div>
            );
        }

        return (
            <div className="action">
                <div className="btn btn-primary" style={actionStyle}>
                    {unsupportedActionTypeText}
                </div>
            </div>
        );
    }
}

const mapStateToProps = ({ ui: { text }, user, config }) => {
    return {
        actionPaymentCompletedText: text.actionPaymentCompleted,
        businessIconUrl: config.businessIconUrl,
        businessName: config.businessName,
        integrations: config.integrations,
        isLinkColorDark: config.colors.isLinkColorDark,
        unsupportedActionTypeText: text.unsupportedActionType,
        user,
    };
};

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            sendPostback,
            immediateUpdate,
            createTransaction,
        },
        dispatch,
    );

export default connect(mapStateToProps, mapDispatchToProps, null, {
    forwardRef: true,
})(ActionComponent);
